import { lazy } from 'react'
import type { ComponentType, LazyExoticComponent } from 'react'

const RELOAD_KEY_PREFIX = 'optioneer:lazy-reload:'
const STALE_CHUNK_ERROR = /failed to fetch dynamically imported module/i

export interface LazyReloadEnvironment {
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  reload: () => void
}

function browserEnvironment(): LazyReloadEnvironment | null {
  if (typeof window === 'undefined') return null

  try {
    return {
      storage: window.sessionStorage,
      reload: () => window.location.reload(),
    }
  } catch {
    return null
  }
}

function isStaleChunkFailure(error: unknown): boolean {
  return error instanceof Error && STALE_CHUNK_ERROR.test(error.message)
}

export async function loadLazyModuleWithReload<T>(
  importer: () => Promise<T>,
  importId: string,
  environment: LazyReloadEnvironment | null = browserEnvironment()
): Promise<T> {
  const reloadKey = `${RELOAD_KEY_PREFIX}${importId}`

  try {
    const importedModule = await importer()
    try {
      environment?.storage.removeItem(reloadKey)
    } catch {
      // Storage cleanup is best effort after a successful import.
    }
    return importedModule
  } catch (error) {
    if (!isStaleChunkFailure(error) || !environment) throw error

    let alreadyReloaded: boolean
    try {
      alreadyReloaded = environment.storage.getItem(reloadKey) === '1'
    } catch {
      throw error
    }

    if (alreadyReloaded) {
      try {
        environment.storage.removeItem(reloadKey)
      } catch {
        // Preserve the original import error even if storage cleanup fails.
      }
      throw error
    }

    try {
      environment.storage.setItem(reloadKey, '1')
    } catch {
      throw error
    }

    environment.reload()
    return new Promise<T>(() => undefined)
  }
}

export function lazyWithReload<T extends ComponentType>(
  importer: () => Promise<{ default: T }>,
  importId: string
): LazyExoticComponent<T> {
  return lazy(() => loadLazyModuleWithReload(importer, importId))
}
